import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import JmlLink from "@/components/JmlLink";
import MarketingShell from "@/components/MarketingShell";
import { PHOTOS } from "@/lib/photos";

export default function Lifecycle() {
  return (
    <MarketingShell active="/private-events">
      <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Private events &middot; Lifecycle celebrations
        </p>
        <h1
          className="leading-[0.95]"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "clamp(40px, 5.6vw, 84px)",
            textWrap: "balance",
            maxWidth: "22ch",
          }}
        >
          For families marking <em style={{ color: "#F2D589" }}>sacred</em> transitions.
        </h1>
      </motion.section>

      <section className="mt-12 grid md:grid-cols-[1.2fr_1fr] gap-10 items-start">
        <div className="space-y-4 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
          <p>
            Weddings, bar/bat mitzvahs, anniversaries, or other sacred
            transitions, I create live piano offerings tailored to
            the tone and meaning of your unique moment.
          </p>
          <p>
            Drawing on Jewish melodic tradition, original compositions,
            and live improvisation, the music can be quiet and reflective
            or joyful and alive, always respectful, responsive, and
            deeply personal.
          </p>
          <ul className="space-y-1.5 pt-2" style={{ color: "rgba(245,239,227,0.78)" }}>
            <li>&middot; Music for before, during, or after ceremony</li>
            <li>&middot; Improvised or thematic (Jewish modes, motifs, or custom requests)</li>
            <li>&middot; Solo, or with collaborators from a network of world-class musicians</li>
            <li>&middot; Sound setup provided when needed</li>
          </ul>
          <p className="italic pt-2" style={{ color: "rgba(245,239,227,0.7)", fontFamily: "'Cormorant Garamond', serif" }}>
            Music not just to accompany, but to hold space. Subtle, present, and attuned to the significance of the event.
          </p>
        </div>
        <div
          className="rounded-2xl overflow-hidden border"
          style={{ borderColor: "rgba(255,255,255,0.12)", aspectRatio: "4 / 5" }}
        >
          <img
            src={PHOTOS.lifecycleEvent}
            alt="Dan playing at a lifecycle celebration"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </section>

      <section className="mt-12 flex flex-wrap gap-3">
        <Link
          to="/contact"
          data-testid="lifecycle-book-cta"
          className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em]"
          style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 600 }}
        >
          Book your lifecycle event &rsaquo;
        </Link>
        <Link
          to="/community-events"
          className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em] border border-white/25 hover:bg-white/10 transition-colors"
        >
          Community events &rsaquo;
        </Link>
      </section>
      <section
        className="mt-14 rounded-2xl border p-6 sm:p-8 backdrop-blur-md"
        style={{ borderColor: "rgba(242,213,137,0.4)", backgroundColor: "rgba(12,8,38,0.6)" }}
        data-testid="lifecycle-commission"
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#F2D589" }}>
          Included with every event
        </p>
        <h2 className="text-2xl sm:text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          A custom commission for your simcha.
        </h2>
        <p className="mt-3 max-w-[48rem] text-sm sm:text-base" style={{ color: "rgba(245,239,227,0.78)", lineHeight: 1.7 }}>
          Every event includes an original piece written for you with the{" "}
          <strong style={{ color: "#F2D589", fontWeight: 400 }}>Alephonic Mapping Method&trade;</strong> — the
          couple&apos;s names, the bar mitzvah boy&apos;s name, the words at the
          heart of the day — mapped letter by letter into music and played live.
          A musical signature that exists for your family and no one else.
        </p>
        <div className="mt-6">
          <JmlLink
            className="inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] border border-[#F2D589]/50 transition-colors hover:bg-[#F2D589]/10"
            style={{ color: "#F2D589" }}
            testId="lifecycle-commission-method"
          >
            How the method works
          </JmlLink>
        </div>
      </section>

    </MarketingShell>
  );
}
